import { ISpecializationCategory } from 'data/interfaces/Common';
import { IArticle } from 'data/interfaces/IArticle';
import ICourse from 'data/interfaces/ICourse';
import { ItemsNotificationType } from 'data/interfaces/INotification';
import { IStats } from 'data/interfaces/IUser';
import { IVacancy } from 'data/interfaces/IVacancy';
import { createContext, useContext, useEffect, useState } from 'react';
import { IPagination } from 'types/types';
import { useUserContext } from './user_state';

interface IState {
  stats: IStats;
  unreadStats: { type: ItemsNotificationType; count: number }[];
  categories: Map<number, ISpecializationCategory>;
  popularCourses: Map<number, ICourse>;
  news: Map<number, IArticle>;
  archiveVacancies: Map<number, IVacancy>;
  activeVacancies: Map<number, IVacancy>;
  init: (
    _stats: IStats,
    _courses: IPagination<ICourse>,
    _categories: ISpecializationCategory[],
    _news: IPagination<IArticle>,
  ) => void;
  setVacancies: (
    activeVacancies: Map<number, IVacancy>,
    archiveVacancies: Map<number, IVacancy>,
  ) => void;
  clearStats: () => void;
  setUnreadStats: (unreadStats: { type: ItemsNotificationType; count: number }[]) => void;
}

const IndexPageContext = createContext<Partial<IState>>({});

interface Props {
  children: React.ReactNode;
}

export function IndexPageWrapper(props: Props) {
  const [stats, setStats] = useState<IStats>();
  const [categories, setCategories] = useState<Map<number, ISpecializationCategory>>(new Map());
  const [popularCourses, setPopularCourses] = useState<Map<number, ICourse>>(new Map());
  const [news, setNews] = useState<Map<number, IArticle>>(new Map());
  const userContext = useUserContext();
  const [archiveVacancies, setArchiveVacanciesState] = useState<Map<number, IVacancy>>(new Map());
  const [activeVacancies, setActiveVacanciesState] = useState<Map<number, IVacancy>>(new Map());
  const [unreadStats, setUnreadStatsState] = useState<
    { type: ItemsNotificationType; count: number }[]
  >([]);

  const init = async (
    _stats: IStats,
    _courses: IPagination<ICourse>,
    _categories: ISpecializationCategory[],
    _news: IPagination<IArticle>,
  ) => {
    if (!userContext.user || !userContext.currentProfile) return;

    setStats(_stats);
    setPopularCourses((state) => {
      _courses.data.forEach((c) => {
        state.set(c.id, c);
      });
      return state;
    });
    setCategories((state) => {
      _categories.forEach((el) => {
        state.set(el.id, el);
      });
      return new Map(state);
    });
    setNews((state) => {
      _news.data.forEach((el) => {
        state.set(el.id, el);
      });
      return state;
    });
  };

  const setVacancies = (
    activeVacancies: Map<number, IVacancy>,
    archiveVacancies: Map<number, IVacancy>,
  ) => {
    setActiveVacanciesState(activeVacancies);
    setArchiveVacanciesState(archiveVacancies);
  };

  const clearStats = () => {
    setStats(undefined);
  };

  const setUnreadStats = (unreadStats: { type: ItemsNotificationType; count: number }[]) => {
    setUnreadStatsState(unreadStats);
  };

  useEffect(() => {
    if (!userContext.currentProfile) {
      clearStats();
      setNews(new Map());
      setCategories(new Map());
      setPopularCourses(new Map());
    }
  }, [userContext.currentProfile, userContext.token]);

  const value: IState = {
    stats,
    categories,
    popularCourses,
    news,
    init,
    clearStats,
    activeVacancies,
    archiveVacancies,
    setVacancies,
    setUnreadStats,
    unreadStats,
  };

  return <IndexPageContext.Provider value={value}>{props.children}</IndexPageContext.Provider>;
}

export function useIndexPageContext() {
  return useContext(IndexPageContext);
}
